/**
 * Phase 39 — a verdict card's SOURCE, pure: the metric catalog and the tag
 * group it brings (today's drops = the gate metrics and DROP tags; the
 * list's or the gateway's own stats = the Flow metrics and that item's
 * tags), the words every line uses, and what switching the source does to
 * the conditions already typed (the ones that read the other numbers go).
 * For Media Devoted | Created By Ciro Autiero
 */
import { type DropGenVerdictCard, type DropGenVerdictSource, type DropGenVerdictSourceKind, FLOW_METRIC_MEANING, type FlowOptimizerMetricMeta, GATE_METRIC_GROUP_LABELS, sourceMetricIds, sourceTagKind, sourceWords } from '@trafficpulse/shared';

import type { DraftVerdictRow } from '../../../../drop-generator.types';
import { isTagRow } from '../../../../drop-generator-rule.types';
import { HOUR_METRIC_CATALOG } from '../../general/hour/formula/hour-metric-catalog';
import { GATE_METRIC_CATALOG } from '../card/gate-metric-catalog';
import { METRIC_MEANING } from '../condition/metric-meanings';

/** Whose tags a card's conditions test. */
export type VerdictTagSurface = 'drops' | 'lists' | 'gateways';

export const TAG_GROUP: Record<VerdictTagSurface, string> = {
  drops: 'Drop tag',
  lists: 'List tag',
  gateways: 'Gateway tag',
};
export const TAG_NOUN: Record<VerdictTagSurface, string> = {
  drops: 'drop',
  lists: 'list',
  gateways: 'gateway',
};

export const tagSurfaceOf = (source: DropGenVerdictSource): VerdictTagSurface => sourceTagKind(source.kind);

/** The picker's catalog: the 23 gate metrics on today's drops, the Flow metrics on a tracker report. */
/** A list's / gateway's stats are the tracker's — the same heading the drops' tracker metrics sit under. */
const TRACKER_CATALOG: FlowOptimizerMetricMeta[] = HOUR_METRIC_CATALOG.map((m) => ({ ...m, section: GATE_METRIC_GROUP_LABELS.tracker }));
export const metricCatalogFor = (kind: DropGenVerdictSourceKind): FlowOptimizerMetricMeta[] => (kind === 'drops' ? GATE_METRIC_CATALOG : TRACKER_CATALOG);

/** What a metric means, in the source's own formulas. */
export const meaningFor = (kind: DropGenVerdictSourceKind, id: string): string | undefined => (kind === 'drops' ? (METRIC_MEANING[id] ?? FLOW_METRIC_MEANING[id]) : FLOW_METRIC_MEANING[id]);

/** "— on today's drops added up." / "— on the list's stats over last 7 days." */
export const onWords = (source: DropGenVerdictSource): string => (source.kind === 'drops' ? "on today's drops added up" : `on ${sourceWords(source, source.kind)}`);

/** The tag line's subject: "any of today's drops" (several drops), "the drop" (hit again), "the list of the day", "the gateway". */
export function tagSubject(source: DropGenVerdictSource, card: DropGenVerdictCard | 'stopLoss'): string {
  if (source.kind === 'list') return 'the list of the day';
  if (source.kind === 'gateway') return 'the gateway';
  return card === 'hitAgain' ? 'the drop' : "any of today's drops";
}

/** The conditions that still make sense on a new source — the other vocabulary's go, and so do the tags (another kind). */
export function rowsForSource(rows: DraftVerdictRow[], kind: DropGenVerdictSourceKind): DraftVerdictRow[] {
  const ids = new Set(sourceMetricIds(kind));
  return rows.filter((r) => !isTagRow(r) && ids.has(r.metric));
}

/** The card's lead for the source pills. */
export const SOURCE_LABELS: Record<DropGenVerdictSourceKind, string> = {
  drops: "Today's drops",
  list: "The list's stats",
  gateway: "The gateway's stats",
};

export const SOURCE_HINT =
  "Today's drops: the drops this container sent today, added up — SMSWiser's sent, delivered and bot counts, the tracker's visits and conversions for those drops — with the DROP tags from Tag Rules › Drops. The item's own stats: what the tracker reports for that list (or gateway) over the date range you pick — the formulas' metrics (CR = conversions per 100 visits), with its own Tag Rules tags. Switching clears the conditions that read the other numbers.";
